import type { IQuoteDocumentTextLine } from '@/interfaces'

/**
 * Model class for quote document-level text lines (headers)
 * Represents special text lines that appear at the document level
 */
export class QuoteDocumentTextLine implements IQuoteDocumentTextLine {
	public lineNum: number
	public orderNumber: number
	public lineType: string
	public lineText: string
	public position: 'HEADER' | 'INLINE'
	public isDeleted: boolean = false

	constructor({
		lineNum,
		orderNumber,
		lineType,
		lineText,
		position,
		isDeleted
	}: Partial<IQuoteDocumentTextLine> = {}) {
		this.lineNum = lineNum || 0
		this.orderNumber = orderNumber || 1
		this.lineType = lineType || 'TEXT'
		this.lineText = lineText || ''
		this.position = position || 'HEADER'
		this.isDeleted = isDeleted || false
	}

	/**
	 * Update the text content of this line
	 */
	public updateText(newText: string): void {
		this.lineText = newText
	}

	/**
	 * Check if this line is empty (has no text content)
	 */
	public isEmpty(): boolean {
		return !this.lineText || this.lineText.trim().length === 0
	}

	/**
	 * Get the display text for this line
	 */
	public getDisplayText(): string {
		return this.lineText || ''
	}

	/**
	 * Mark this line as deleted
	 */
	public markDeleted(): void {
		this.isDeleted = true
	}

	/**
	 * Check if this line is valid
	 */
	public isValid(): boolean {
		return !this.isEmpty() && this.orderNumber > 0
	}

	/**
	 * Get validation errors for this line
	 */
	public getValidationErrors(): string[] {
		const errors: string[] = []

		if (this.isEmpty()) {
			errors.push('Text line cannot be empty')
		}

		if (this.orderNumber <= 0) {
			errors.push('Order number must be greater than 0')
		}

		return errors
	}

	/**
	 * Convert to JSON for API serialization
	 */
	public toJSON(): Record<string, unknown> {
		return {
			lineNum: this.lineNum,
			orderNumber: this.orderNumber,
			lineType: this.lineType,
			lineText: this.lineText,
			position: this.position,
			isDeleted: this.isDeleted
		}
	}
}
